import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  fetchTrip,
  updateStatus,
  addNote,
  updateNextAction,
  getEdit,
  STATUS_LIST,
  formatDate,
  formatDateTime,
} from "./adminApi";
import { AdminHeader } from "./AdminDashboard";

const DURATION = { weekend: "Weekend", "4-7": "4–7 days", "8-14": "8–14 days", "2+weeks": "2+ weeks", not_sure: "Not sure" };
const cap = (s) => (s ? s.toString().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "—");

const Field = ({ label, value }) => (
  <div>
    <p className="font-sans text-[0.65rem] uppercase tracking-[0.18em] text-sand">{label}</p>
    <p className="mt-1 font-sans text-sm text-cream">{value || "—"}</p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="border-t border-charcoal py-6">
    <h2 className="mb-4 font-sans text-xs uppercase tracking-[0.24em] text-teal">{title}</h2>
    {children}
  </div>
);

export const AdminTripDetail = () => {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [note, setNote] = useState("");
  const [na, setNa] = useState("");
  const [naDate, setNaDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [editInfo, setEditInfo] = useState(null); // null=unknown, false=none, obj=exists

  const load = useCallback(async () => {
    const d = await fetchTrip(id);
    setT(d);
    setNa(d.next_action || "");
    setNaDate(d.next_action_date || "");
    try {
      setEditInfo(await getEdit(id));
    } catch {
      setEditInfo(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!t)
    return (
      <div className="min-h-screen bg-ink text-cream">
        <AdminHeader />
        <p className="p-10 font-sans text-sm text-sand">Loading…</p>
      </div>
    );

  const onStatus = async (status) => {
    setSaving(true);
    try {
      setT(await updateStatus(id, status));
    } finally {
      setSaving(false);
    }
  };
  const onAddNote = async () => {
    if (!note.trim()) return;
    setT(await addNote(id, note.trim()));
    setNote("");
  };
  const onSaveNextAction = async () => {
    setSaving(true);
    try {
      setT(await updateNextAction(id, na || null, naDate || null));
    } finally {
      setSaving(false);
    }
  };

  const dur = DURATION[t.trip_duration] || t.trip_duration || "—";
  const briefLine = [t.destination || "Undecided", dur, cap(t.traveller_type)].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen overflow-x-hidden bg-ink text-cream" data-testid="admin-trip-detail">
      <AdminHeader />
      <main className="mx-auto max-w-[1100px] px-6 py-8 sm:px-10">
        <Link to="/admin/trips" className="font-sans text-xs uppercase tracking-[0.18em] text-sand hover:text-teal">
          ← All requests
        </Link>

        {/* Holideviser Trip Brief */}
        <div className="mt-6 border border-teal/40 bg-teal/[0.06] p-6 sm:p-8" data-testid="trip-brief">
          <p className="font-sans text-[0.65rem] uppercase tracking-[0.28em] text-teal">Holideviser Trip Brief</p>
          <h1 className="mt-3 break-words font-serif text-2xl font-light uppercase tracking-tight text-cream sm:text-4xl">
            {briefLine}
          </h1>
          <p className="mt-3 font-sans text-sm text-sand">From {t.origin}</p>
          <p className="mt-4 font-sans text-base text-cream">{(t.interests || []).join(" · ") || "—"}</p>
          <p className="mt-1 font-sans text-sm text-sand">
            {cap(t.accommodation_preference)} · {cap(t.travel_pace)} pace
          </p>
          {t.things_to_avoid && (
            <p className="mt-3 font-sans text-sm text-cream">
              <span className="text-teal">Avoid:</span> {t.things_to_avoid}
            </p>
          )}
        </div>

        {/* The Holideviser Edit CTA */}
        <div className="mt-6 flex flex-col items-start justify-between gap-4 border border-charcoal bg-charcoal/20 p-6 sm:flex-row sm:items-center" data-testid="edit-cta">
          <div>
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.28em] text-teal">The Holideviser Edit</p>
            <p className="mt-2 font-sans text-sm text-cream">
              {editInfo
                ? `Personalised trip plan — ${editInfo.status === "PUBLISHED" ? "Published (link is live)" : "Draft in progress"}`
                : "Craft a personalised, editorial trip plan for this traveller."}
            </p>
          </div>
          <Link
            to={`/admin/trips/${id}/edit`}
            data-testid="open-edit-btn"
            className="shrink-0 rounded-none bg-teal px-6 py-3 font-sans text-xs uppercase tracking-[0.18em] text-ink hover:bg-cream"
          >
            {editInfo ? "Open the Edit" : "Create the Edit"}
          </Link>
        </div>

        {/* Ops controls */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Section title="Trip overview">
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                <Field label="Destination" value={t.destination} />
                <Field label="Also considering" value={(t.additional_destinations || []).join(", ")} />
                <Field label="Origin" value={t.origin} />
                <Field label="Dates" value={t.travel_dates} />
                <Field label="Flexibility" value={cap(t.date_flexibility)} />
                <Field label="Duration" value={dur} />
                <Field label="Travellers" value={t.traveller_count} />
                <Field label="Trip type" value={cap(t.traveller_type)} />
                <Field label="Destination certainty" value={cap(t.destination_certainty)} />
              </div>
            </Section>

            <Section title="Travel style & what they want">
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                <Field label="Pace" value={cap(t.travel_pace)} />
                <Field label="Tourist preference" value={cap(t.tourist_preference)} />
                <Field label="Accommodation" value={cap(t.accommodation_preference)} />
              </div>
              <div className="mt-5">
                <Field label="Interests" value={(t.interests || []).join(" · ")} />
              </div>
            </Section>

            <Section title="What they don't want">
              <Field label="Things to avoid" value={t.things_to_avoid} />
            </Section>

            <Section title="Budget">
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                <Field label="Tier" value={cap(t.budget_tier)} />
                <Field label="Currency" value={t.currency} />
                <Field label="Amount" value={t.budget_amount} />
              </div>
            </Section>

            <Section title="Contact">
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                <Field label="Name" value={t.customer_name} />
                <Field label="Email" value={t.email} />
                <Field label="Phone / WhatsApp" value={t.phone} />
                <Field label="Instagram" value={t.instagram} />
              </div>
            </Section>

            <Section title="Additional notes">
              <Field label="Customer message" value={t.additional_notes} />
            </Section>
          </div>

          {/* Side panel */}
          <div className="lg:col-span-1">
            <div className="border border-charcoal p-5">
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-sand">Status</p>
              <select
                value={t.status}
                onChange={(e) => onStatus(e.target.value)}
                disabled={saving}
                data-testid="detail-status-select"
                className="mt-2 w-full rounded-none border border-charcoal bg-charcoal/30 px-3 py-2 font-sans text-sm text-cream focus:border-teal focus:outline-none"
              >
                {STATUS_LIST.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <p className="mt-6 font-sans text-xs uppercase tracking-[0.2em] text-sand">Next action</p>
              <input
                value={na}
                onChange={(e) => setNa(e.target.value)}
                placeholder="e.g. Send WhatsApp"
                data-testid="detail-next-action"
                className="mt-2 w-full rounded-none border border-charcoal bg-charcoal/30 px-3 py-2 font-sans text-sm text-cream focus:border-teal focus:outline-none"
              />
              <input
                type="date"
                value={naDate}
                onChange={(e) => setNaDate(e.target.value)}
                data-testid="detail-next-action-date"
                className="mt-2 w-full rounded-none border border-charcoal bg-charcoal/30 px-3 py-2 font-sans text-sm text-cream focus:border-teal focus:outline-none"
              />
              <button
                onClick={onSaveNextAction}
                disabled={saving}
                data-testid="detail-save-next-action"
                className="mt-3 w-full rounded-none bg-teal px-4 py-2 font-sans text-xs uppercase tracking-[0.16em] text-ink hover:bg-cream disabled:opacity-60"
              >
                Save next action
              </button>
            </div>

            {/* Internal notes */}
            <div className="mt-5 border border-charcoal p-5">
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-sand">Internal notes</p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Add an internal note…"
                data-testid="detail-note-input"
                className="mt-2 w-full resize-none rounded-none border border-charcoal bg-charcoal/30 px-3 py-2 font-sans text-sm text-cream focus:border-teal focus:outline-none"
              />
              <button
                onClick={onAddNote}
                data-testid="detail-add-note"
                className="mt-2 w-full rounded-none border border-teal px-4 py-2 font-sans text-xs uppercase tracking-[0.16em] text-teal hover:bg-teal hover:text-ink"
              >
                Add note
              </button>
              <div className="mt-4 flex flex-col gap-3" data-testid="notes-list">
                {(t.internal_notes || []).slice().reverse().map((n, i) => (
                  <div key={i} className="border-l border-teal/50 pl-3">
                    <p className="font-sans text-sm text-cream">{n.text}</p>
                    <p className="mt-1 font-sans text-[0.65rem] text-sand">{formatDateTime(n.ts)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity history */}
            <div className="mt-5 border border-charcoal p-5">
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-sand">Activity</p>
              <div className="mt-3 flex flex-col gap-2" data-testid="activity-history">
                {(t.activity_history || []).slice().reverse().map((a, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-3">
                    <span className="font-sans text-xs text-cream">{a.action}</span>
                    <span className="shrink-0 font-sans text-[0.6rem] text-sand">{formatDateTime(a.ts)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
